import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { LegacyOnlyRoute } from '@/components/auth/LegacyOnlyRoute';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { AppLayout } from '@/components/layout/AppLayout';
import { useAuth } from '@/hooks/useAuth';
import { useTenant } from '@/hooks/useTenant';

// Lazy load das páginas para code splitting
const LandingPage = lazy(() => import('@/pages/Landing').then(module => ({ default: module.default })));
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const OrdersPage = lazy(() => import('@/pages/OrdersPage'));
const ClientsPage = lazy(() => import('@/pages/ClientsPage'));
const ProductsPage = lazy(() => import('@/pages/ProductsPage'));
const ReportsPage = lazy(() => import('@/pages/ReportsPage'));
const BillingPage = lazy(() => import('@/pages/BillingPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const SubscriptionsPage = lazy(() => import('@/pages/SubscriptionsPage'));
const HelpCenterPage = lazy(() => import('@/pages/HelpCenterPage'));
const UsersPage = lazy(() => import('@/pages/UsersPage'));
const RifasPage = lazy(() => import('@/pages/RifasPage'));
const NewRequestPage = lazy(() => import('@/pages/Marketplace/NewRequestPage'));
const MyRequestsPage = lazy(() => import('@/pages/Marketplace/MyRequestsPage'));
const LeadsPage = lazy(() => import('@/pages/Marketplace/LeadsPage'));
const ProfessionalProfilePage = lazy(() => import('@/pages/Marketplace/ProfessionalProfilePage'));
const MarketplacePaymentsAdminPage = lazy(
  () => import('@/pages/Marketplace/MarketplacePaymentsAdminPage'),
);
const ProfessionalPublicPage = lazy(() => import('@/pages/Marketplace/ProfessionalPublicPage'));

// Componente de loading para Suspense
const PageLoader = () => (
  <div className="flex min-h-screen items-center justify-center bg-slate-50">
    <div className="flex flex-col items-center gap-4">
      <div className="size-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600"></div>
      <p className="text-sm text-slate-500">Carregando...</p>
    </div>
  </div>
);

const App = () => {
  const { isLoadingUser, user } = useAuth();
  const { slug, tenant, isLoading: isLoadingTenant } = useTenant();
  const supportEmail = tenant?.contactEmail ?? 'pedidooonline@gmail.com';

  if (isLoadingUser || (slug && isLoadingTenant)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <p className="text-sm text-slate-500">Carregando informações...</p>
      </div>
    );
  }

  if (slug && !tenant) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 px-6 text-center">
        <h1 className="text-2xl font-semibold text-slate-800">
          Não encontramos o ambiente solicitado
        </h1>
        <p className="mt-4 max-w-lg text-sm text-slate-500">
          Verifique se o endereço foi digitado corretamente ou entre em contato com nosso time de suporte.
        </p>
        <a
          className="mt-6 inline-flex items-center rounded-full bg-primary-600 px-5 py-2 text-sm font-medium text-white shadow hover:bg-primary-700"
          href={`mailto:${supportEmail}`}
        >
          Falar com suporte
        </a>
      </div>
    );
  }

  if (slug && tenant?.status && tenant.status !== 'ACTIVE') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-slate-950 px-6 text-center text-slate-100">
        <h1 className="text-3xl font-semibold">Ambiente temporariamente indisponível</h1>
        <p className="mt-4 max-w-lg text-sm text-slate-300">
          O ambiente <span className="font-semibold text-white">{tenant.name}</span> está com o status{' '}
          <span className="uppercase tracking-wide text-primary-300">{tenant.status.toLowerCase()}</span>. Nosso time já foi notificado.
        </p>
        <a
          className="mt-6 inline-flex items-center rounded-full bg-primary-500 px-5 py-2 text-sm font-medium text-white shadow hover:bg-primary-600"
          href={`mailto:${supportEmail}`}
        >
          Entrar em contato
        </a>
      </div>
    );
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route
          path="/"
          element={user ? <Navigate to="/app" replace /> : <LandingPage />}
        />
        <Route
          path="/login"
          element={user ? <Navigate to="/app" replace /> : <LandingPage />}
        />
        <Route path="/help" element={<HelpCenterPage />} />
        <Route path="/pedir-orcamento" element={<NewRequestPage />} />
        <Route path="/meus-pedidos" element={<MyRequestsPage />} />
        <Route path="/profissionais/:id" element={<ProfessionalPublicPage />} />
        <Route element={<ProtectedRoute />}>
          <Route path="/app" element={<AppLayout />}>
            <Route element={<LegacyOnlyRoute />}>
              <Route index element={<DashboardPage />} />
              <Route path="orders" element={<OrdersPage />} />
            </Route>
            <Route path="clients" element={<ClientsPage />} />
            <Route path="products" element={<ProductsPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="billing" element={<BillingPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="subscriptions" element={<SubscriptionsPage />} />
            <Route path="rifas" element={<RifasPage />} />
            <Route path="leads" element={<LeadsPage />} />
            <Route path="perfil-profissional" element={<ProfessionalProfilePage />} />
            <Route path="marketplace-pagamentos" element={<MarketplacePaymentsAdminPage />} />
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
};

export default App;
